import cs from 'classnames';
import { useField, useFormikContext } from 'formik';
import React from 'react';
import { CustomOption, Typeahead, TypeaheadState } from 'react-bootstrap-typeahead';
import { useElementId } from 'shared/utils/elementIds';
import { getFormikFieldServerErrors } from 'shared/forms/helpers';
import styles from 'shared/formik/TextField.module.scss';


interface Props extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  name: string;
  label?: string;
  valid?: boolean
  options: string[]
}
export const OccupationTypeahead = ({label, className, options, ...props}: Props) => {

  const [field, meta] = useField(props)
  const fieldValue = field.value as string;
  const context = useFormikContext();
  const serverErrors = getFormikFieldServerErrors(context.status, field.name);
  const elementId = useElementId("occupation-typeahead");

  // Need to store the typed in value so that we can use that value onBlur
  const [inputValue, setInputValue] = React.useState(field.value || '');

  // Need to store the state of focused to know whether to show the label or not
  const [focused, setFocused] = React.useState(false);

  const optionsWithCurrent = Array.from(options);
  if (fieldValue && !options.includes(fieldValue)) {
    optionsWithCurrent.push(fieldValue);
    optionsWithCurrent.sort();
  }

  const ref = React.useRef<Typeahead<string>>(null);
  // const hint = useTypeaheadHintValue({ref});


  // console.log('current hint / select is:', hint, ref.current?.state)

  return (
    <div className={cs("form-group", className, styles.FormField)}>
      <Typeahead<string>
        id={elementId}
        ref={ref}
        placeholder={label + '.  Start typing...'}
        options={optionsWithCurrent}
        selected={fieldValue ? [fieldValue] : []}
        allowNew
        newSelectionPrefix="Add new occupation: "
        onChange={(selected) => {
          // console.log('onChange',)
          // The typing for "selected" is wrong, so force typing
          const value = selected[0] || "" as string | CustomOption;
          const strValue = (typeof value === "string") ? value : value.label;
          context.setFieldValue(props.name, strValue)
          setInputValue(strValue);
          if (strValue) {
            context.setErrors({...context.errors, [props.name]: undefined});
          }
        }}
        inputProps={{
          className: cs(
            meta.touched && (meta.error || serverErrors) && 'is-invalid', 
            props.valid && 'is-valid'
          ),
        }}
        onInputChange={(input, e) => {
          // console.log(ref)
          setInputValue(input);
        }}
        onFocus={() => {
          setInputValue(field.value || '')       
          setFocused(true);
        }}
        onBlur={() => {
          if (ref.current) {
            const state = ref.current?.state as TypeaheadState<string>;
            // Only type to auto complete the field if a value is not selected
            if (state.selected.length === 0) {
              const newValue = getTypeaheadHintValue(state);
              if (newValue) {
                context.setFieldValue(field.name, newValue);
              }
              else {
                // So that the value is cleared from the field
                // Otherwise it appears that there is a value, when there is not
                ref.current?.clear();
                // So that the "required" error shows up
                context.setFieldTouched(props.name);
              }
            }
          }
        }}
      />
      <label className={(field.value || inputValue || focused) ? styles.visibleLabel : ''}>{label}</label>
      {meta.touched && meta.error && 
        <div className="text-danger small mt-1">{meta.error}</div>
      }
    </div>
  )
}


// Is a hint visible that should be selected?
// Used onBlur to decide whether to set the field value for this.
const getTypeaheadHintValue = (state: TypeaheadState<string>) => {

  if (state.selected.length === 0) {

    const { activeItem, initialItem, text } = state;
            
    // If an item in the dropdown is selected, use that value
    if (activeItem) {
      // Pre-defined occupation
      if (typeof activeItem === "string") {
        return activeItem
      }
      // Custom occupation
      else {
        return activeItem.label
      }
    }
    
    // Select the hint shown if nothing is selected in the dropdown
    else if (typeof initialItem === "string") {
      // The hint appears to be visible if the first item in the list
      // starts with the value typed
      if (text.length > 0 && initialItem.toLocaleLowerCase().startsWith(text.toLocaleLowerCase())) {
        return initialItem
      }
    }


  }

  // There must not be a hint
  return '';
}